import Snackbar from '@/components/elements/notifications/Snackbar/Snackbar';
import { navigate } from '@/helpers/embed';
import { bookingFlowTrackingSession } from '@/helpers/checkoutFlowTrackingSession';
import { _s } from '@/locale';
import { createKlarnaMerchantOrder } from '@/services/klarnaServices';
import { InitKlarnaResponse } from '@/types/api/services/booking';
import { KPAuthorizationResponse } from '@/types/klarnaPayments';
import { Dispatch, useState } from 'react';
import { toast } from 'react-toastify';

type Status = 'SUBMITTING' | 'ERROR' | 'INITIAL';

type SubmitKlarnaBookingContext = {
  klarnaOrder: InitKlarnaResponse;
  employeeId: number;
  successCallback: ({ redirectURL }: { redirectURL: string }) => void;
  errorCallback: () => void;
};

type KlarnaBookingCheckoutAPI = {
  onSubmitKlarna: (res: KPAuthorizationResponse) => Promise<void>;
};

type KlarnaBookingCheckoutManager = {
  klarnaBookingCheckoutAPI: () => KlarnaBookingCheckoutAPI;
  status: Status;
};

function handleOnSubmitKlarna(context: SubmitKlarnaBookingContext, dispatch: Dispatch<Status>) {
  return async (res: KPAuthorizationResponse) => {
    dispatch('SUBMITTING');
    const createRes = await createKlarnaMerchantOrder(
      context.klarnaOrder.sessionID,
      res.authorization_token,
      `${context.employeeId}`,
    );

    if (!createRes.redirectURL) {
      context.errorCallback();
      return;
    }
    context.successCallback({ redirectURL: createRes.redirectURL });
  };
}

const useKlarnaBookingCheckoutManager = ({
  klarnaOrder,
  employeeId,
}: {
  klarnaOrder: InitKlarnaResponse;
  employeeId: number;
}): KlarnaBookingCheckoutManager => {
  const [status, setStatus] = useState<Status>('INITIAL');

  const handleKlarnaError = () => {
    bookingFlowTrackingSession.checkoutPaymentMethod('error', 'klarna');

    setStatus('ERROR');
    toast(({ closeToast }) => <Snackbar label={_s('serverError')} type="danger" onClose={closeToast} />, {
      autoClose: false,
    });
  };

  const handleKlarnaSuccess = ({ redirectURL }) => {
    bookingFlowTrackingSession.checkoutPaymentMethod('success', 'klarna', { flush: true });
    navigate(redirectURL);
  };

  const klarnaBookingCheckoutAPI = (): KlarnaBookingCheckoutAPI => {
    const errorCallback = handleKlarnaError;
    const successCallback = handleKlarnaSuccess;
    const context = {
      klarnaOrder,
      employeeId,
      errorCallback,
      successCallback,
    };

    return {
      onSubmitKlarna: handleOnSubmitKlarna(context, setStatus),
    };
  };

  return {
    klarnaBookingCheckoutAPI,
    status,
  };
};

export default useKlarnaBookingCheckoutManager;
